import { ADDON_ID, PANEL_ID, PARAM_KEY, EVENTS } from './chunk-DA5ZZG53.mjs';
import * as React12 from 'react';
import React12__default, { useState, useCallback, useMemo, Fragment } from 'react';
import { addons, types, useAddonState, useStorybookApi, useChannel, useParameter, useStorybookState } from '@storybook/manager-api';
import { Badge, Icons, WithTooltip, TooltipLinkList, IconButton, ActionBar, ScrollArea, Spaced, Placeholder } from '@storybook/components';
import { styled, convert, themes, Global } from '@storybook/theming';
import { useResizeDetector } from 'react-resize-detector';
import { STORY_RENDERED, STORY_CHANGED } from '@storybook/core-events';
import { HIGHLIGHT } from '@storybook/addon-highlight';

var Filters = (props) => /* @__PURE__ */ React12.createElement("svg", { ...props }, /* @__PURE__ */ React12.createElement("defs", null, /* @__PURE__ */ React12.createElement("filter", { id: "protanopia" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.567, 0.433, 0, 0, 0 0.558, 0.442, 0, 0, 0 0, 0.242, 0.758, 0, 0 0, 0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "protanomaly" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.817, 0.183, 0, 0, 0 0.333, 0.667, 0, 0, 0 0, 0.125, 0.875, 0, 0 0, 0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "deuteranopia" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.625, 0.375, 0, 0, 0 0.7, 0.3, 0, 0, 0 0, 0.3, 0.7, 0, 0 0, 0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "deuteranomaly" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.8, 0.2, 0, 0, 0 0.258, 0.742, 0, 0, 0 0, 0.142, 0.858, 0, 0 0, 0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "tritanopia" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.95, 0.05,  0, 0, 0 0,  0.433, 0.567, 0, 0 0,  0.475, 0.525, 0, 0 0,  0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "tritanomaly" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.967, 0.033, 0, 0, 0 0, 0.733, 0.267, 0, 0 0, 0.183, 0.817, 0, 0 0, 0, 0, 1, 0"
  }
)), /* @__PURE__ */ React12.createElement("filter", { id: "achromatopsia" }, /* @__PURE__ */ React12.createElement(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    values: "0.299, 0.587, 0.114, 0, 0 0.299, 0.587, 0.114, 0, 0 0.299, 0.587, 0.114, 0, 0 0, 0, 0, 1, 0"
  }
))));

// src/components/VisionSimulator.tsx
var iframeId = "storybook-preview-iframe";
var baseList = [
  { name: "blurred vision", percentage: 22.9 },
  { name: "deuteranomaly", percentage: 2.7 },
  { name: "deuteranopia", percentage: 0.56 },
  { name: "protanomaly", percentage: 0.66 },
  { name: "protanopia", percentage: 0.59 },
  { name: "tritanomaly", percentage: 0.01 },
  { name: "tritanopia", percentage: 0.016 },
  { name: "achromatopsia", percentage: 1e-4 },
  { name: "grayscale" }
];
var getFilter = (filterName) => {
  if (!filterName) {
    return "none";
  }
  if (filterName === "blurred vision") {
    return "blur(2px)";
  }
  if (filterName === "grayscale") {
    return "grayscale(100%)";
  }
  return `url('#${filterName}')`;
};
var Hidden = styled.div(() => ({
  "&, & svg": {
    position: "absolute",
    width: 0,
    height: 0
  }
}));
var ColorIcon = styled.span(
  {
    background: "linear-gradient(to right, #F44336, #FF9800, #FFEB3B, #8BC34A, #2196F3, #9C27B0)",
    borderRadius: "1rem",
    display: "block",
    height: "1rem",
    width: "1rem"
  },
  ({ filter }) => ({
    filter: getFilter(filter)
  }),
  ({ theme }) => ({
    boxShadow: `${theme.appBorderColor} 0 0 0 1px inset`
  })
);
var Column = styled.span({
  display: "flex",
  flexDirection: "column"
});
var Title = styled.span({
  textTransform: "capitalize"
});
var Description = styled.span(({ theme }) => ({
  fontSize: 11,
  color: theme.textMutedColor
}));
var getColorList = (active, set) => [
  ...active !== null ? [
    {
      id: "reset",
      title: "Reset color filter",
      onClick: () => {
        set(null);
      },
      right: void 0,
      active: false
    }
  ] : [],
  ...baseList.map((i) => {
    const description = i.percentage !== void 0 ? `${i.percentage}% of users` : void 0;
    return {
      id: i.name,
      title: /* @__PURE__ */ React12__default.createElement(Column, null, /* @__PURE__ */ React12__default.createElement(Title, null, i.name), description && /* @__PURE__ */ React12__default.createElement(Description, null, description)),
      onClick: () => {
        set(i);
      },
      right: /* @__PURE__ */ React12__default.createElement(ColorIcon, { filter: i.name }),
      active: active === i
    };
  })
];
var VisionSimulator = () => {
  const [filter, setFilter] = useState(null);
  return /* @__PURE__ */ React12__default.createElement(React12__default.Fragment, null, filter && /* @__PURE__ */ React12__default.createElement(
    Global,
    {
      styles: {
        [`#${iframeId}`]: {
          filter: getFilter(filter.name)
        }
      }
    }
  ), /* @__PURE__ */ React12__default.createElement(
    WithTooltip,
    {
      placement: "top",
      tooltip: ({ onHide }) => {
        const colorList = getColorList(filter, (i) => {
          setFilter(i);
          onHide();
        });
        return /* @__PURE__ */ React12__default.createElement(TooltipLinkList, { links: colorList });
      },
      closeOnOutsideClick: true,
      onDoubleClick: () => setFilter(null)
    },
    /* @__PURE__ */ React12__default.createElement(IconButton, { key: "filter", active: !!filter, title: "Vision simulator" }, /* @__PURE__ */ React12__default.createElement(Icons, { icon: "accessibility" }))
  ), /* @__PURE__ */ React12__default.createElement(Hidden, null, /* @__PURE__ */ React12__default.createElement(Filters, null)));
};
var Wrapper = styled.div({
  padding: 12,
  marginBottom: 10
});
var Description2 = styled.p({
  margin: "0 0 12px"
});
var Link = styled.a({
  marginTop: 12,
  textDecoration: "underline",
  color: "inherit",
  display: "block"
});
var Info = ({ item }) => {
  return /* @__PURE__ */ React12__default.createElement(Wrapper, null, /* @__PURE__ */ React12__default.createElement(Description2, null, item.description), /* @__PURE__ */ React12__default.createElement(Link, { href: item.helpUrl, target: "_blank" }, "More info..."));
};
var List = styled.div({
  display: "flex",
  flexDirection: "column",
  paddingBottom: 4,
  paddingRight: 4,
  paddingTop: 4,
  fontWeight: 400
});
var Item = styled.div(({ elementWidth }) => {
  const maxWidthBeforeBreak = 407;
  return {
    flexDirection: elementWidth > maxWidthBeforeBreak ? "row" : "inherit",
    marginBottom: elementWidth > maxWidthBeforeBreak ? 6 : 12,
    display: elementWidth > maxWidthBeforeBreak ? "flex" : "block"
  };
});
var StyledBadge = styled(Badge)({
  padding: "2px 8px",
  marginBottom: 3,
  minWidth: 65,
  maxWidth: "fit-content",
  width: "100%",
  textAlign: "center"
});
var Message = styled.div({
  paddingLeft: 6,
  paddingRight: 23
});
var formatSeverityText = (severity) => {
  return severity.charAt(0).toUpperCase().concat(severity.slice(1));
};
var Rule = ({ rule }) => {
  const { ref, width } = useResizeDetector({
    refreshMode: "debounce",
    handleHeight: false,
    handleWidth: true
  });
  let badgeType = null;
  switch (rule.impact) {
    case "critical" /* CRITICAL */:
      badgeType = "critical";
      break;
    case "serious" /* SERIOUS */:
      badgeType = "negative";
      break;
    case "moderate" /* MODERATE */:
      badgeType = "warning";
      break;
    case "minor" /* MINOR */:
      badgeType = "neutral";
      break;
  }
  return /* @__PURE__ */ React12__default.createElement(Item, { ref, elementWidth: width || 0 }, /* @__PURE__ */ React12__default.createElement(StyledBadge, { status: badgeType }, formatSeverityText(rule.impact)), /* @__PURE__ */ React12__default.createElement(Message, null, rule.message));
};
var Rules = ({ rules }) => {
  return /* @__PURE__ */ React12__default.createElement(List, null, rules.map((rule, index) => (
    // eslint-disable-next-line react/no-array-index-key
    /* @__PURE__ */ React12__default.createElement(Rule, { rule, key: index })
  )));
};
var colorsByType = [
  convert(themes.light).color.negative,
  // VIOLATION,
  convert(themes.light).color.positive,
  // PASS,
  convert(themes.light).color.warning
  // INCOMPLETION,
];
var A11yContext = React12.createContext({
  results: {
    passes: [],
    incomplete: [],
    violations: []
  },
  setResults: () => {
  },
  highlighted: [],
  toggleHighlight: () => {
  },
  clearHighlights: () => {
  },
  tab: 0,
  setTab: () => {
  }
});
var defaultResult = {
  passes: [],
  incomplete: [],
  violations: []
};
var A11yContextProvider = ({ active, ...props }) => {
  const [results, setResults] = useAddonState(ADDON_ID, defaultResult);
  const [tab, setTab] = React12.useState(0);
  const [highlighted, setHighlighted] = React12.useState([]);
  const api = useStorybookApi();
  const storyEntry = api.getCurrentStoryData();
  const handleToggleHighlight = React12.useCallback((target, highlight) => {
    setHighlighted(
      (prevHighlighted) => highlight ? [...prevHighlighted, ...target] : prevHighlighted.filter((t) => !target.includes(t))
    );
  }, []);
  const handleRun = (renderedStoryId) => {
    emit(EVENTS.REQUEST, renderedStoryId);
  };
  const handleClearHighlights = React12.useCallback(() => setHighlighted([]), []);
  const handleSetTab = React12.useCallback((index) => {
    handleClearHighlights();
    setTab(index);
  }, []);
  const handleReset = React12.useCallback(() => {
    setTab(0);
    setResults(defaultResult);
  }, []);
  const emit = useChannel({
    [STORY_RENDERED]: handleRun,
    [STORY_CHANGED]: handleReset
  });
  React12.useEffect(() => {
    emit(HIGHLIGHT, { elements: highlighted, color: colorsByType[tab] });
  }, [highlighted, tab]);
  React12.useEffect(() => {
    if (active && storyEntry?.type === "story") {
      handleRun(storyEntry.id);
    } else {
      handleClearHighlights();
    }
  }, [active, handleClearHighlights, emit, storyEntry]);
  if (!active)
    return null;
  return /* @__PURE__ */ React12.createElement(
    A11yContext.Provider,
    {
      value: {
        results,
        setResults,
        highlighted,
        toggleHighlight: handleToggleHighlight,
        clearHighlights: handleClearHighlights,
        tab,
        setTab: handleSetTab
      },
      ...props
    }
  );
};
var useA11yContext = () => React12.useContext(A11yContext);

// src/components/Report/HighlightToggle.tsx
var Checkbox = styled.input(({ disabled }) => ({
  cursor: disabled ? "not-allowed" : "pointer"
}));
function areAllRequiredElementsHighlighted(elementsToHighlight, highlighted) {
  const highlightedCount = elementsToHighlight.filter(
    (item) => highlighted.includes(item.target[0])
  ).length;
  return highlightedCount === 0 ? 1 /* UNCHECKED */ : highlightedCount === elementsToHighlight.length ? 0 /* CHECKED */ : 2 /* INDETERMINATE */;
}
var HighlightToggle = ({ toggleId, elementsToHighlight = [] }) => {
  const { toggleHighlight, highlighted } = useA11yContext();
  const checkBoxRef = React12__default.useRef(null);
  const [checkBoxState, setChecked] = React12__default.useState(
    areAllRequiredElementsHighlighted(elementsToHighlight, highlighted)
  );
  React12__default.useEffect(() => {
    const newState = areAllRequiredElementsHighlighted(elementsToHighlight, highlighted);
    if (checkBoxRef.current) {
      checkBoxRef.current.indeterminate = newState === 2 /* INDETERMINATE */;
    }
    setChecked(newState);
  }, [elementsToHighlight, highlighted]);
  const handleToggle = React12__default.useCallback(() => {
    toggleHighlight(
      elementsToHighlight.map((e) => e.target[0]),
      checkBoxState !== 0 /* CHECKED */
    );
  }, [elementsToHighlight, checkBoxState, toggleHighlight]);
  return /* @__PURE__ */ React12__default.createElement(
    Checkbox,
    {
      ref: checkBoxRef,
      id: toggleId,
      type: "checkbox",
      "aria-label": "Highlight result",
      disabled: !elementsToHighlight.length,
      onChange: handleToggle,
      checked: checkBoxState === 0 /* CHECKED */
    }
  );
};
var HighlightToggle_default = HighlightToggle;

// src/components/Report/Elements.tsx
var Item2 = styled.li({
  fontWeight: 600
});
var ItemTitle = styled.span(({ theme }) => ({
  borderBottom: `1px solid ${theme.appBorderColor}`,
  width: "100%",
  display: "flex",
  paddingBottom: 6,
  marginBottom: 6,
  justifyContent: "space-between"
}));
var HighlightToggleElement = styled.span({
  fontWeight: "normal",
  alignSelf: "center",
  paddingRight: 15,
  input: {
    margin: 0,
    display: "block"
  }
});
var Element = ({ element, type }) => {
  const { any, all, none } = element;
  const rules = [...any, ...all, ...none];
  const highlightToggleId = `${type}-${element.target[0]}`;
  return /* @__PURE__ */ React12__default.createElement(Item2, null, /* @__PURE__ */ React12__default.createElement(ItemTitle, null, element.target[0], /* @__PURE__ */ React12__default.createElement(HighlightToggleElement, null, /* @__PURE__ */ React12__default.createElement(HighlightToggle_default, { toggleId: highlightToggleId, elementsToHighlight: [element] }))), /* @__PURE__ */ React12__default.createElement(Rules, { rules }));
};
var Elements = ({ elements, type }) => /* @__PURE__ */ React12__default.createElement("ol", null, elements.map((element, index) => (
  // eslint-disable-next-line react/no-array-index-key
  /* @__PURE__ */ React12__default.createElement(Element, { element, key: index, type })
)));
var Wrapper2 = styled.div({
  display: "flex",
  flexWrap: "wrap",
  margin: "12px 0"
});
var Item3 = styled.div(({ theme }) => ({
  margin: "0 6px",
  padding: 5,
  border: `1px solid ${theme.appBorderColor}`,
  borderRadius: theme.appBorderRadius
}));
var Tags = ({ tags }) => {
  return /* @__PURE__ */ React12__default.createElement(Wrapper2, null, tags.map((tag) => /* @__PURE__ */ React12__default.createElement(Item3, { key: tag }, tag)));
};

// src/components/Report/Item.tsx
var Wrapper3 = styled.div(({ theme }) => ({
  display: "flex",
  width: "100%",
  borderBottom: `1px solid ${theme.appBorderColor}`,
  "&:hover": {
    background: theme.background.hoverable
  }
}));
var Icon = styled(Icons)(({ theme }) => ({
  height: 10,
  width: 10,
  minWidth: 10,
  color: theme.textMutedColor,
  marginRight: 10,
  transition: "transform 0.1s ease-in-out",
  alignSelf: "center",
  display: "inline-flex"
}));
var HeaderBar = styled.div(({ theme }) => ({
  padding: theme.layoutMargin,
  paddingLeft: theme.layoutMargin - 3,
  lineHeight: "20px",
  background: "none",
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
  borderLeft: "3px solid transparent",
  width: "100%",
  "&:focus": {
    outline: "0 none",
    borderLeft: `3px solid ${theme.color.secondary}`
  }
}));
var HighlightToggleElement2 = styled.span({
  fontWeight: "normal",
  float: "right",
  marginRight: 15,
  alignSelf: "center",
  input: {
    margin: 0,
    display: "block"
  }
});
var Item4 = (props) => {
  const [open, onToggle] = useState(false);
  const { item, type } = props;
  const highlightToggleId = `${type}-${item.id}`;
  return /* @__PURE__ */ React12__default.createElement(Fragment, null, /* @__PURE__ */ React12__default.createElement(Wrapper3, null, /* @__PURE__ */ React12__default.createElement(HeaderBar, { onClick: () => onToggle(!open), role: "button" }, /* @__PURE__ */ React12__default.createElement(
    Icon,
    {
      icon: "arrowdown",
      color: "#9DA5AB",
      style: {
        transform: `rotate(${open ? 0 : -90}deg)`
      }
    }
  ), item.help), /* @__PURE__ */ React12__default.createElement(HighlightToggleElement2, null, /* @__PURE__ */ React12__default.createElement(HighlightToggle_default, { toggleId: highlightToggleId, elementsToHighlight: item.nodes }))), open ? /* @__PURE__ */ React12__default.createElement(Fragment, null, /* @__PURE__ */ React12__default.createElement(Info, { item, key: "info" }), /* @__PURE__ */ React12__default.createElement(Elements, { elements: item.nodes, type, key: "elements" }), /* @__PURE__ */ React12__default.createElement(Tags, { tags: item.tags, key: "tags" })) : null);
};

// src/components/Report/index.tsx
var Report = ({ items, empty, type }) => /* @__PURE__ */ React12__default.createElement(Fragment, null, items && items.length ? items.map((item) => /* @__PURE__ */ React12__default.createElement(Item4, { item, key: `${type}:${item.id}`, type })) : /* @__PURE__ */ React12__default.createElement(Placeholder, { key: "placeholder" }, empty));
var Container = styled.div({
  width: "100%",
  position: "relative",
  minHeight: "100%"
});
var HighlightToggleLabel = styled.label(({ theme }) => ({
  cursor: "pointer",
  userSelect: "none",
  color: theme.color.dark
}));
var GlobalToggle = styled.div(({ elementWidth, theme }) => {
  const maxWidthBeforeBreak = 450;
  return {
    cursor: "pointer",
    fontSize: 13,
    lineHeight: "20px",
    padding: elementWidth > maxWidthBeforeBreak ? "10px 15px 10px 0" : "10px 0px 10px 15px",
    height: "40px",
    border: "none",
    marginTop: elementWidth > maxWidthBeforeBreak ? -40 : 0,
    float: elementWidth > maxWidthBeforeBreak ? "right" : "left",
    display: "flex",
    alignItems: "center",
    width: elementWidth > maxWidthBeforeBreak ? "auto" : "100%",
    borderBottom: elementWidth > maxWidthBeforeBreak ? "none" : `1px solid ${theme.appBorderColor}`,
    input: {
      marginLeft: 10,
      marginRight: 0,
      marginTop: -1,
      marginBottom: 0
    }
  };
});
var Item5 = styled.button(
  ({ theme }) => ({
    textDecoration: "none",
    padding: "10px 15px",
    cursor: "pointer",
    fontWeight: theme.typography.weight.bold,
    fontSize: theme.typography.size.s2 - 1,
    lineHeight: 1,
    height: 40,
    border: "none",
    borderTop: "3px solid transparent",
    borderBottom: "3px solid transparent",
    background: "transparent",
    "&:focus": {
      outline: "0 none",
      borderBottom: `3px solid ${theme.color.secondary}`
    }
  }),
  ({ active, theme }) => active ? {
    opacity: 1,
    borderBottom: `3px solid ${theme.color.secondary}`
  } : {}
);
var TabsWrapper = styled.div({});
var List2 = styled.div(({ theme }) => ({
  boxShadow: `${theme.appBorderColor} 0 -1px 0 0 inset`,
  background: theme.background.app,
  display: "flex",
  justifyContent: "space-between",
  whiteSpace: "nowrap"
}));
function retrieveAllNodesFromResults(items) {
  return items.reduce((acc, item) => acc.concat(item.nodes), []);
}
var Tabs = ({ tabs }) => {
  const { ref, width } = useResizeDetector({
    refreshMode: "debounce",
    handleHeight: false,
    handleWidth: true
  });
  const { tab: activeTab, setTab } = useA11yContext();
  const handleToggle = React12.useCallback(
    (event) => {
      setTab(parseInt(event.currentTarget.getAttribute("data-index") || "", 10));
    },
    [setTab]
  );
  const highlightToggleId = `${tabs[activeTab].type}-global-checkbox`;
  const highlightLabel = `Highlight results`;
  return /* @__PURE__ */ React12.createElement(Container, { ref }, /* @__PURE__ */ React12.createElement(List2, null, /* @__PURE__ */ React12.createElement(TabsWrapper, null, tabs.map((tab, index) => /* @__PURE__ */ React12.createElement(
    Item5,
    {
      key: index,
      "data-index": index,
      active: activeTab === index,
      onClick: handleToggle
    },
    tab.label
  )))), tabs[activeTab].items.length > 0 ? /* @__PURE__ */ React12.createElement(GlobalToggle, { elementWidth: width || 0 }, /* @__PURE__ */ React12.createElement(HighlightToggleLabel, { htmlFor: highlightToggleId }, highlightLabel), /* @__PURE__ */ React12.createElement(
    HighlightToggle_default,
    {
      toggleId: highlightToggleId,
      elementsToHighlight: retrieveAllNodesFromResults(tabs[activeTab].items)
    }
  )) : null, tabs[activeTab].panel);
};

// src/components/A11YPanel.tsx
var Icon2 = styled(Icons)({
  height: 12,
  width: 12,
  marginRight: 4
});
var RotatingIcon = styled(Icon2)(({ theme }) => ({
  animation: `${theme.animation.rotate360} 1s linear infinite;`
}));
var Passes = styled.span(({ theme }) => ({
  color: theme.color.positiveText
}));
var Violations = styled.span(({ theme }) => ({
  color: theme.color.negativeText
}));
var Incomplete = styled.span(({ theme }) => ({
  color: theme.color.warningText
}));
var Centered = styled.span({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%"
});
var A11YPanel = () => {
  const { manual } = useParameter("a11y", {
    manual: false
  });
  const [status, setStatus] = useState(manual ? "manual" : "initial");
  const [error, setError] = React12__default.useState(void 0);
  const { setResults, results } = useA11yContext();
  const { storyId } = useStorybookState();
  React12__default.useEffect(() => {
    setStatus(manual ? "manual" : "initial");
  }, [manual]);
  const handleResult = (axeResults) => {
    setStatus("ran");
    setResults(axeResults);
    setTimeout(() => {
      if (status === "ran") {
        setStatus("ready");
      }
    }, 900);
  };
  const handleRun = useCallback(() => {
    setStatus("running");
  }, []);
  const handleError = useCallback((err) => {
    setStatus("error");
    setError(err);
  }, []);
  const emit = useChannel({
    [EVENTS.RUNNING]: handleRun,
    [EVENTS.RESULT]: handleResult,
    [EVENTS.ERROR]: handleError
  });
  const handleManual = useCallback(() => {
    setStatus("running");
    emit(EVENTS.MANUAL, storyId);
  }, [storyId]);
  const manualActionItems = useMemo(
    () => [{ title: "Run test", onClick: handleManual }],
    [handleManual]
  );
  const readyActionItems = useMemo(
    () => [
      {
        title: status === "ready" ? "Rerun tests" : /* @__PURE__ */ React12__default.createElement(React12__default.Fragment, null, /* @__PURE__ */ React12__default.createElement(Icon2, { icon: "check" }), " Tests completed"),
        onClick: handleManual
      }
    ],
    [status, handleManual]
  );
  const tabs = useMemo(() => {
    const { passes, incomplete, violations } = results;
    return [
      {
        label: /* @__PURE__ */ React12__default.createElement(Violations, null, violations.length, " Violations"),
        panel: /* @__PURE__ */ React12__default.createElement(
          Report,
          {
            items: violations,
            type: 0 /* VIOLATION */,
            empty: "No accessibility violations found."
          }
        ),
        items: violations,
        type: 0 /* VIOLATION */
      },
      {
        label: /* @__PURE__ */ React12__default.createElement(Passes, null, passes.length, " Passes"),
        panel: /* @__PURE__ */ React12__default.createElement(Report, { items: passes, type: 1 /* PASS */, empty: "No accessibility checks passed." }),
        items: passes,
        type: 1 /* PASS */
      },
      {
        label: /* @__PURE__ */ React12__default.createElement(Incomplete, null, incomplete.length, " Incomplete"),
        panel: /* @__PURE__ */ React12__default.createElement(
          Report,
          {
            items: incomplete,
            type: 2 /* INCOMPLETION */,
            empty: "No accessibility checks incomplete."
          }
        ),
        items: incomplete,
        type: 2 /* INCOMPLETION */
      }
    ];
  }, [results]);
  return /* @__PURE__ */ React12__default.createElement(React12__default.Fragment, null, status === "initial" && /* @__PURE__ */ React12__default.createElement(Centered, null, "Initializing..."), status === "manual" && /* @__PURE__ */ React12__default.createElement(React12__default.Fragment, null, /* @__PURE__ */ React12__default.createElement(Centered, null, "Manually run the accessibility scan."), /* @__PURE__ */ React12__default.createElement(ActionBar, { key: "actionbar", actionItems: manualActionItems })), status === "running" && /* @__PURE__ */ React12__default.createElement(Centered, null, /* @__PURE__ */ React12__default.createElement(RotatingIcon, { icon: "sync" }), " Please wait while the accessibility scan is running ..."), (status === "ready" || status === "ran") && /* @__PURE__ */ React12__default.createElement(React12__default.Fragment, null, /* @__PURE__ */ React12__default.createElement(ScrollArea, { vertical: true, horizontal: true }, /* @__PURE__ */ React12__default.createElement(Tabs, { key: "tabs", tabs })), /* @__PURE__ */ React12__default.createElement(ActionBar, { key: "actionbar", actionItems: readyActionItems })), status === "error" && /* @__PURE__ */ React12__default.createElement(Centered, null, "The accessibility scan encountered an error.", /* @__PURE__ */ React12__default.createElement("br", null), typeof error === "string" ? error : JSON.stringify(error)));
};

// src/manager.tsx
var Title2 = () => {
  const [addonState] = useAddonState(ADDON_ID);
  const violationsNb = addonState?.violations?.length || 0;
  const incompleteNb = addonState?.incomplete?.length || 0;
  const count = violationsNb + incompleteNb;
  const suffix = count === 0 ? "" : /* @__PURE__ */ React12__default.createElement(Badge, { status: "neutral" }, count);
  return /* @__PURE__ */ React12__default.createElement("div", null, /* @__PURE__ */ React12__default.createElement(Spaced, { col: 1 }, /* @__PURE__ */ React12__default.createElement("span", { style: { display: "inline-block", verticalAlign: "middle" } }, "Accessibility"), suffix));
};
addons.register(ADDON_ID, (api) => {
  addons.add(PANEL_ID, {
    title: "",
    type: types.TOOL,
    match: ({ viewMode }) => viewMode === "story",
    render: () => /* @__PURE__ */ React12__default.createElement(VisionSimulator, null)
  });
  addons.add(PANEL_ID, {
    title: Title2,
    type: types.PANEL,
    render: ({ active = true }) => /* @__PURE__ */ React12__default.createElement(A11yContextProvider, { active }, /* @__PURE__ */ React12__default.createElement(A11YPanel, null)),
    paramKey: PARAM_KEY
  });
});
